/**
 * Rule of 4 and 2 — "you have N outs on street X, approximate equity?"
 *
 * The mental rule every live player uses:
 *   - **Flop** (2 cards to come): equity ≈ outs × 4
 *   - **Turn** (1 card to come): equity ≈ outs × 2
 *
 * It's an approximation. Exact flop equity = 1 − (1 − N/47)(1 − N/46),
 * which equals N×4 only at low out counts. The rule overestimates at
 * 9+ outs on the flop (rule says 60% for 15 outs; actual is ~54%).
 * For a v1 quiz we teach the *rule*, not the exact equity — that's
 * what players actually use at the table.
 *
 * Procedural questions: pick a street + an out count, the answer is
 * N × multiplier. Distractors include "wrong-street" answers (using
 * ×4 when the street called for ×2) to train the street/multiplier
 * association directly.
 */

import { pickFrom, shuffle } from "@/features/quiz/lib/common";

export type Street = "flop" | "turn";

const OUT_COUNTS = [2, 4, 6, 8, 9, 12, 15] as const;
const STREETS: readonly Street[] = ["flop", "turn"] as const;

export interface RuleOf42Question {
  outs: number;
  street: Street;
  /** Correct equity in percentage points (e.g. 36). */
  answer: number;
  options: number[];
  correctIndex: number;
}

export function equityByRule(outs: number, street: Street): number {
  return street === "flop" ? outs * 4 : outs * 2;
}

export function generateQuestion(
  rand: () => number = Math.random,
): RuleOf42Question {
  const outs = pickFrom(OUT_COUNTS, rand);
  const street = pickFrom(STREETS, rand);
  const answer = equityByRule(outs, street);

  // Distractors: meaningful confusions.
  //  - wrong street: the rule applied for the other street
  //  - off-by-one out count: nearby canonical out → nearby equity
  //  - far: a clearly different equity
  const wrongStreet: Street = street === "flop" ? "turn" : "flop";
  const wrongStreetAnswer = equityByRule(outs, wrongStreet);

  const nearOuts = OUT_COUNTS.filter((o) => o !== outs).sort(
    (a, b) => Math.abs(a - outs) - Math.abs(b - outs),
  );
  const nearAnswer = equityByRule(nearOuts[0]!, street);
  const farAnswer = equityByRule(nearOuts[nearOuts.length - 1]!, street);

  const raw = new Set<number>([answer, wrongStreetAnswer, nearAnswer, farAnswer]);
  // Sometimes wrongStreetAnswer collides with answer (e.g. outs=0
  // hypothetically) or with nearAnswer. Backfill from other canonical
  // values until we have 4 distinct options.
  while (raw.size < 4) {
    const filler = pickFrom(OUT_COUNTS, rand) * (street === "flop" ? 4 : 2);
    raw.add(filler);
  }
  const options = shuffle([...raw].slice(0, 4), rand);
  return { outs, street, answer, options, correctIndex: options.indexOf(answer) };
}
